/*
 * Scrolling gallery interaction.
 *
 * Two modes, chosen per section by data-sg-mode:
 *
 *   marquee  (default) - the section's own CSS animation drives the track.
 *                        This file only hands control back and forth:
 *                          autoplay -> the CSS animation drives the track
 *                          manual   -> the animation is switched off and the same
 *                                      position is expressed as the viewport's
 *                                      native scrollLeft, so touch panning is real
 *                                      browser scrolling with real momentum
 *                        The handover converts between the two so it never jumps.
 *
 *   carousel           - no animation at all. A finite, user-driven horizontal
 *                        scroller: native touch scrolling, mouse drag, and
 *                        arrows that scroll the real viewport one card at a time.
 */
(function () {
  'use strict';

  var RESUME_DELAY = 180;

  function init(root) {
    if (root.__sgReady) return;
    root.__sgReady = true;

    var viewport = root.querySelector('[data-sg-viewport]');
    var track = root.querySelector('[data-sg-track]');
    if (!viewport || !track) return;

    initVideos(track);

    if ((root.getAttribute('data-sg-mode') || 'marquee') === 'carousel') {
      initCarousel(root, viewport, track);
      return;
    }

    var pauseOnPress = root.getAttribute('data-sg-pause') === 'true';
    var canDrag = root.getAttribute('data-sg-drag') === 'true';
    if (!pauseOnPress && !canDrag) return;

    var manual = false;
    var pressed = false;
    var wrapping = false;
    var idleTimer = null;
    var mouse = null;

    function anim() {
      if (!track.getAnimations) return null;
      var list = track.getAnimations();
      for (var i = 0; i < list.length; i++) {
        if (list[i].animationName === 'scrollGallery') return list[i];
      }
      return list[0] || null;
    }

    function duration(a) {
      var d = a && a.effect && a.effect.getTiming().duration;
      return typeof d === 'number' && d > 0 ? d : 0;
    }

    // The CSS animation travels -50% of the track, so this is what maps
    // animation progress <-> pixels.
    function half() {
      return track.scrollWidth / 2;
    }

    // The distance after which the duplicated cards repeat exactly. It differs
    // from half() by one gap, so shifting or wrapping by this value is
    // pixel-perfect where half() would jump.
    function period() {
      var items = track.children;
      var n = items.length / 2;
      if (items.length >= 2 && items.length % 2 === 0 && items[n]) {
        return items[n].offsetLeft - items[0].offsetLeft;
      }
      return track.scrollWidth / 2;
    }

    /* --- pause-only mode (manual swipe disabled) --------------------- */

    function pauseAuto() {
      var a = anim();
      if (a && a.playState === 'running') a.pause();
    }

    function resumeAuto() {
      var a = anim();
      if (a && a.playState !== 'running') a.play();
    }

    /* --- autoplay <-> native scrolling handover ---------------------- */

    function enterManual() {
      if (manual) return;
      var a = anim();
      var d = duration(a);
      var progress = d ? ((((a.currentTime || 0) % d) + d) % d) / d : 0;
      var offset = progress * half();

      // One exact repeat-period of runway so the visitor can swipe backwards
      // too. Shifting by the period lands on identical pixels.
      var p = period();
      if (p > viewport.clientWidth * 2) offset += p;

      manual = true;
      wrapping = true;
      track.classList.add('sg-manual'); // animation:none -> transform resets to none
      viewport.scrollLeft = viewport.scrollLeft + offset;
      wrapping = false;
    }

    function exitManual() {
      if (!manual) return;
      var h = half();
      var p = period();
      var offset = p ? ((viewport.scrollLeft % p) + p) % p : viewport.scrollLeft;
      var progress = h ? (offset % h) / h : 0;

      manual = false;
      wrapping = true;
      viewport.scrollLeft = 0;
      track.classList.remove('sg-manual');
      wrapping = false;

      var a = anim();
      if (a) {
        var d = duration(a);
        if (d) a.currentTime = progress * d;
        a.play();
      }
    }

    function scheduleResume() {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        idleTimer = null;
        if (!pressed && manual) exitManual();
      }, RESUME_DELAY);
    }

    /* --- infinite loop while the visitor is scrolling ---------------- */

    function onScroll() {
      if (!manual) {
        // pause-on-press off + drag on: engage only once real movement starts,
        // so a plain press does not stop the animation.
        if (canDrag && pressed) enterManual();
        return;
      }
      if (wrapping) return;

      var p = period();
      if (p > viewport.clientWidth * 2) {
        var x = viewport.scrollLeft;
        var next = x;
        if (x > p * 1.5) next = x - p;
        else if (x < p * 0.5) next = x + p;
        if (next !== x) {
          wrapping = true;
          viewport.scrollLeft = next;
          wrapping = false;
        }
      }
      scheduleResume();
    }

    /* --- pointer handling ------------------------------------------- */

    function onDown(e) {
      pressed = true;
      if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }

      if (canDrag) {
        if (pauseOnPress) enterManual();
        if (e.pointerType === 'mouse') {
          mouse = { x: e.clientX, left: viewport.scrollLeft, active: false };
        }
      } else if (pauseOnPress) {
        pauseAuto();
      }
    }

    function onMove(e) {
      if (!mouse) return;
      var dx = e.clientX - mouse.x;
      if (!mouse.active) {
        if (Math.abs(dx) < 4) return;
        mouse.active = true;
        viewport.classList.add('sg-dragging');
        if (!manual) enterManual();
        // re-baseline: entering manual mode changes scrollLeft
        mouse.x = e.clientX;
        mouse.left = viewport.scrollLeft;
        dx = 0;
      }
      e.preventDefault();
      viewport.scrollLeft = mouse.left - dx;
    }

    function onUp() {
      pressed = false;
      if (mouse) {
        viewport.classList.remove('sg-dragging');
        mouse = null;
      }
      if (manual) scheduleResume();
      else if (pauseOnPress) resumeAuto();
    }

    viewport.addEventListener('pointerdown', onDown);
    viewport.addEventListener('scroll', onScroll, { passive: true });
    // move/up on the window so a drag that leaves the strip still finishes cleanly
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /* --- videos: never audible, and only decoding while on screen ------ */

  function initVideos(track) {
    var videos = track.querySelectorAll('video');
    if (!videos.length) return;
    for (var i = 0; i < videos.length; i++) {
      videos[i].muted = true;
      videos[i].playsInline = true;
    }
    if (!window.IntersectionObserver) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { rootMargin: '100px' });
    for (var j = 0; j < videos.length; j++) io.observe(videos[j]);
  }

  /* --- static carousel: finite, user-driven, no animation ------------ */

  function initCarousel(root, viewport, track) {
    var prev = root.querySelector('[data-sg-prev]');
    var next = root.querySelector('[data-sg-next]');
    var mouse = null;

    // one card plus the gap, measured so it follows the responsive card width
    function step() {
      var items = track.children;
      if (items.length > 1) return items[1].offsetLeft - items[0].offsetLeft;
      if (items.length === 1) return items[0].getBoundingClientRect().width + 20;
      return viewport.clientWidth;
    }

    function maxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function sync() {
      var max = maxScroll();
      // nothing to scroll -> centre the cards rather than leave a gap at the end
      if (max <= 1) viewport.classList.add('sg-fits');
      else viewport.classList.remove('sg-fits');
      if (prev) prev.disabled = viewport.scrollLeft <= 1;
      if (next) next.disabled = viewport.scrollLeft >= max - 1;
    }

    function nudge(dir) {
      // the arrows drive the real scroll container - there is no second state
      var to = Math.max(0, Math.min(maxScroll(), viewport.scrollLeft + dir * step()));
      if (viewport.scrollTo) viewport.scrollTo({ left: to, behavior: 'smooth' });
      else viewport.scrollLeft = to;
    }

    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });

    viewport.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;   // touch uses native scrolling
      mouse = { x: e.clientX, left: viewport.scrollLeft, active: false };
    });
    window.addEventListener('pointermove', function (e) {
      if (!mouse) return;
      var dx = e.clientX - mouse.x;
      if (!mouse.active) {
        if (Math.abs(dx) < 4) return;          // threshold so clicks stay clicks
        mouse.active = true;
        viewport.classList.add('sg-dragging');
        mouse.x = e.clientX;
        mouse.left = viewport.scrollLeft;
        dx = 0;
      }
      e.preventDefault();
      viewport.scrollLeft = mouse.left - dx;
    }, { passive: false });
    function endDrag() {
      if (!mouse) return;
      viewport.classList.remove('sg-dragging');
      mouse = null;
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    sync();
    window.addEventListener('load', sync);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(sync);
      ro.observe(viewport);  // container width: responsive breakpoints, rotation
      ro.observe(track);     // card widths settle once media has laid out
    }
  }

  function scan(scope) {
    var roots = (scope || document).querySelectorAll('[data-sg-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Theme Editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
